import { Button } from '@/components/ui/Button';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import { cn } from '@/lib/cn';

export type WeworkPushTimeRangeRow = {
  id: string;
  start: string;
  end: string;
};

type Props = {
  ranges: WeworkPushTimeRangeRow[];
  minIntervalMinutes: number;
  onRangesChange: (ranges: WeworkPushTimeRangeRow[]) => void;
  onMinIntervalChange: (minutes: number) => void;
  disabled?: boolean;
};

function newRow(start = '09:00', end = '18:00'): WeworkPushTimeRangeRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    start,
    end,
  };
}

export function parseTimeRangesFromApi(
  raw: Array<{ start?: string; end?: string }> | undefined | null,
): WeworkPushTimeRangeRow[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [newRow('07:00', '22:00')];
  }
  const rows: WeworkPushTimeRangeRow[] = [];
  raw.forEach((item, idx) => {
    const start = (item.start || '').trim();
    const end = (item.end || '').trim();
    if (!/^\d{1,2}:\d{2}$/.test(start) || !/^\d{1,2}:\d{2}$/.test(end) || start === end) {
      return;
    }
    rows.push({ id: `r-${idx}`, start, end });
  });
  return rows.length > 0 ? rows : [newRow('07:00', '22:00')];
}

/** 保存前校验，返回错误文案；通过返回 null */
export function validateWeworkPushSchedule(
  ranges: WeworkPushTimeRangeRow[],
  minIntervalMinutes: number,
): string | null {
  const valid = ranges.filter((r) => {
    const s = r.start.trim();
    const e = r.end.trim();
    return /^\d{1,2}:\d{2}$/.test(s) && /^\d{1,2}:\d{2}$/.test(e) && s !== e;
  });
  if (valid.length < 1) {
    return '请至少配置一个有效推送时段（开始与结束时间须为 HH:MM 且不能相同）';
  }
  if (minIntervalMinutes < 0 || minIntervalMinutes > 10080) {
    return '最小推送间隔须在 0～10080 分钟之间（0 表示仅按上方频次规则）';
  }
  return null;
}

export function WeworkPushTimeRangesEditor({
  ranges,
  minIntervalMinutes,
  onRangesChange,
  onMinIntervalChange,
  disabled = false,
}: Props) {
  function updateRow(id: string, patch: Partial<Pick<WeworkPushTimeRangeRow, 'start' | 'end'>>) {
    onRangesChange(ranges.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function removeRow(id: string) {
    if (ranges.length <= 1) {
      return;
    }
    onRangesChange(ranges.filter((r) => r.id !== id));
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className={labelClass}>允许推送时段</span>
          <Button
            type="button"
            variant="secondary"
            disabled={disabled || ranges.length >= 24}
            onClick={() => onRangesChange([...ranges, newRow()])}
          >
            添加时段
          </Button>
        </div>
        <p className="mb-3 text-xs text-slate-500">
          可配置多个时间段，精确到分钟；当前时刻落在任一时段内才会推送。跨午夜请用「22:00–07:00」形式。
        </p>
        <ul className="space-y-2">
          {ranges.map((row, index) => (
            <li
              key={row.id}
              className={cn(
                'flex flex-wrap items-center gap-2 rounded-lg border border-slate-200/90 bg-white px-3 py-2.5',
              )}
            >
              <span className="w-6 shrink-0 text-center text-[11px] font-medium text-slate-400">
                {index + 1}
              </span>
              <input
                type="time"
                className={cn(inputClass, 'w-[7.5rem] font-mono text-sm')}
                value={row.start}
                disabled={disabled}
                onChange={(e) => updateRow(row.id, { start: e.target.value })}
              />
              <span className="text-xs text-slate-500">至</span>
              <input
                type="time"
                className={cn(inputClass, 'w-[7.5rem] font-mono text-sm')}
                value={row.end}
                disabled={disabled}
                onChange={(e) => updateRow(row.id, { end: e.target.value })}
              />
              <Button
                type="button"
                variant="secondary"
                className="ml-auto shrink-0 text-xs"
                disabled={disabled || ranges.length <= 1}
                onClick={() => removeRow(row.id)}
              >
                删除
              </Button>
            </li>
          ))}
        </ul>
      </div>

      <label className="flex max-w-xs flex-col gap-1.5">
        <span className={labelClass}>每次推送最小间隔（分钟）</span>
        <input
          type="number"
          min={0}
          max={10080}
          step={1}
          className={inputClass}
          value={minIntervalMinutes}
          disabled={disabled}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            onMinIntervalChange(Number.isFinite(n) ? Math.max(0, n) : 0);
          }}
        />
        <span className="text-xs text-slate-500">
          两次成功推送之间至少相隔该分钟数（与「推送默认频次」同时生效）。填 0 表示不额外限制，仅按频次日历规则。
        </span>
      </label>
    </div>
  );
}
